import { useEffect, useState } from "react";
import QRCode from "qrcode";
import { ConfirmButton } from "@/components/common/ConfirmButton";
import { InputField } from "@/components/common/InputField";
import type { RecoveryCodes, TotpEnrollment } from "@/lib/mfa-api";
import { MfaCodeInput } from "./MfaCodeInput";

const QR_SIZE = 168;

interface TotpSetupFormProps {
	start: () => Promise<TotpEnrollment>;
	complete: (state: string, code: string) => Promise<RecoveryCodes>;
	onDone: (recoveryCodes: RecoveryCodes) => void;
	/** 失敗をその場で出せないもの (ログイン待ちの期限切れなど) を親に渡す。 */
	onFatalError?: (error: unknown) => boolean;
	onCancel?: () => void;
}

/** 認証アプリ (TOTP) の登録。QR コードと手入力用のキーを見せ、表示されたコードで登録を確定する。 */
export function TotpSetupForm({
	start,
	complete,
	onDone,
	onFatalError,
	onCancel,
}: TotpSetupFormProps) {
	const [enrollment, setEnrollment] = useState<TotpEnrollment | null>(null);
	const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
	const [code, setCode] = useState("");
	const [error, setError] = useState<string | null>(null);
	const [submitting, setSubmitting] = useState(false);

	useEffect(() => {
		let cancelled = false;
		start()
			.then(async (started) => {
				const url = await QRCode.toDataURL(started.uri, { margin: 1, width: QR_SIZE });
				if (cancelled) return;
				setEnrollment(started);
				setQrDataUrl(url);
			})
			.catch((err: unknown) => {
				if (cancelled || onFatalError?.(err)) return;
				console.error("認証アプリの登録を始められませんでした", err);
				setError(err instanceof Error ? err.message : "認証アプリの登録を始められませんでした。");
			});
		return () => {
			cancelled = true;
		};
	}, [start, onFatalError]);

	async function handleSubmit(e: React.FormEvent) {
		e.preventDefault();
		if (!enrollment) return;
		setSubmitting(true);
		setError(null);
		try {
			onDone(await complete(enrollment.state, code));
		} catch (err) {
			if (onFatalError?.(err)) return;
			setError(err instanceof Error ? err.message : "認証アプリを登録できませんでした。");
		} finally {
			setSubmitting(false);
		}
	}

	return (
		<form
			onSubmit={handleSubmit}
			data-testid="totp-setup-form"
			style={{ display: "flex", flexDirection: "column", gap: 12 }}
		>
			<p
				style={{
					margin: 0,
					fontSize: "var(--text-ui)",
					color: "var(--fg-2)",
					lineHeight: "var(--leading-body)",
				}}
			>
				Google Authenticator や Microsoft Authenticator などの認証アプリで QR
				コードを読み取り、表示された 6 桁のコードを入力してください。
			</p>
			{enrollment && qrDataUrl && (
				<div style={{ display: "flex", gap: 16, alignItems: "center" }}>
					<img
						src={qrDataUrl}
						width={QR_SIZE}
						height={QR_SIZE}
						alt="認証アプリ用の QR コード"
						style={{ flex: "none", borderRadius: "var(--radius-3)" }}
					/>
					<div style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 0 }}>
						<span style={{ fontSize: "var(--text-caption)", color: "var(--fg-3)" }}>
							読み取れない場合はこのキーを入力
						</span>
						<code
							className="select-text"
							data-testid="totp-secret"
							style={{
								fontFamily: "var(--font-mono)",
								fontSize: "var(--text-caption)",
								color: "var(--fg-1)",
								wordBreak: "break-all",
							}}
						>
							{enrollment.secret.replace(/(.{4})/g, "$1 ").trim()}
						</code>
					</div>
				</div>
			)}
			<InputField label="認証アプリのコード" error={error ?? undefined}>
				<MfaCodeInput value={code} onChange={setCode} kind="totp" testId="totp-setup-code" />
			</InputField>
			<div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
				{onCancel && (
					<ConfirmButton label="キャンセル" variant="ghost" loading={false} onClick={onCancel} />
				)}
				<ConfirmButton
					type="submit"
					label="登録"
					loading={submitting}
					disabled={!enrollment || code.trim().length === 0}
					data-testid="totp-setup-submit"
				/>
			</div>
		</form>
	);
}
